import React, { useEffect, useState } from "react";
import { FileCheck, Phone, Mail, Bell, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import { adminApi, errorMessage } from "../../lib/api";

function dueBadge(it) {
  if (it.overdue) return <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700">Overdue {Math.abs(it.days_left)}d</span>;
  if (it.days_left <= 10) return <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">Due in {it.days_left}d</span>;
  return <span className="rounded-full bg-surface-100 px-2.5 py-1 text-xs font-semibold text-ink-soft">In {it.days_left}d</span>;
}

function fmtReminded(ts) {
  if (!ts) return null;
  try { return new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return null; }
}

export default function RekycExpiry() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    adminApi.rekycExpiring().then((d) => setItems(d.items || [])).catch((e) => { toast.error(errorMessage(e)); setItems([]); });
  }, []);

  const remind = async (it) => {
    setBusy(it.user_id);
    try {
      const r = await adminApi.rekycRemind(it.user_id);
      setItems((prev) => prev.map((x) => (x.user_id === it.user_id ? { ...x, last_reminded: r.last_reminded } : x)));
      toast.success(`Reminder sent to ${it.name}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  const overdue = (items || []).filter((i) => i.overdue).length;
  const soon = (items || []).filter((i) => !i.overdue && i.days_left <= 10).length;

  return (
    <>
      <PageHeader title="Re-KYC expiry" description="Verified partners by nearest re-verification due date — plan your follow-ups." />
      {items === null && <LoadingBlock rows={4} />}
      {items?.length === 0 && <EmptyState icon={FileCheck} title="No re-KYC dates set" text="Partners get a re-KYC date when you approve or set one on the verification pages." />}
      {items?.length > 0 && (
        <>
          <div className="mb-5 flex flex-wrap gap-3" data-testid="rekyc-summary">
            <div className="kt-card px-4 py-3"><p className="text-xs uppercase tracking-wider text-ink-muted">Overdue</p><p className="text-2xl font-bold text-red-600">{overdue}</p></div>
            <div className="kt-card px-4 py-3"><p className="text-xs uppercase tracking-wider text-ink-muted">Due ≤ 10 days</p><p className="text-2xl font-bold text-amber-600">{soon}</p></div>
            <div className="kt-card px-4 py-3"><p className="text-xs uppercase tracking-wider text-ink-muted">Total scheduled</p><p className="text-2xl font-bold text-ink">{items.length}</p></div>
          </div>
          <div className="kt-card overflow-hidden">
            <table className="w-full text-sm" data-testid="rekyc-table">
              <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted">
                <tr><th className="px-5 py-3 font-semibold">Partner</th><th className="hidden px-5 py-3 font-semibold sm:table-cell">Type</th><th className="hidden px-5 py-3 font-semibold md:table-cell">Contact</th><th className="px-5 py-3 font-semibold">Due date</th><th className="px-5 py-3 font-semibold">Status</th><th className="px-5 py-3 font-semibold">Reminder</th></tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                {items.map((it) => (
                  <tr key={it.user_id} className={`hover:bg-surface-50 ${it.overdue ? "bg-red-50/40" : ""}`} data-testid={`rekyc-row-${it.user_id}`}>
                    <td className="px-5 py-4">
                      <p className="font-medium text-ink">{it.restaurant_name || it.name}</p>
                      <p className="text-xs text-ink-soft">{it.restaurant_name ? it.name : it.email}</p>
                    </td>
                    <td className="hidden px-5 py-4 capitalize text-ink-soft sm:table-cell">{it.role}</td>
                    <td className="hidden px-5 py-4 md:table-cell">
                      <div className="flex flex-col gap-1 text-xs text-ink-soft">
                        {it.phone && <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1 hover:text-brand"><Phone className="h-3 w-3" /> {it.phone}</a>}
                        {it.email && <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1 hover:text-brand"><Mail className="h-3 w-3" /> {it.email}</a>}
                      </div>
                    </td>
                    <td className="px-5 py-4 font-medium text-ink">{new Date(it.rekyc_due + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-5 py-4">{dueBadge(it)}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => remind(it)} disabled={busy === it.user_id} data-testid={`rekyc-remind-${it.user_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
                        {busy === it.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />} Remind
                      </button>
                      {it.last_reminded && <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-muted"><Check className="h-3 w-3 text-brand" /> {fmtReminded(it.last_reminded)}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
